import { GoogleMapsOverlayProps } from '@deck.gl/google-maps/typed';
import { HereMapInterface } from 'common/utils/Map';

import {
  HereMarkerInterface,
  OverlayProps,
  HereLayers,
  HereLayer,
} from '../types';

import { MarkersProvider } from './MarkersProvider';
import { getClusterPresentation } from './getClusterPresentation';
import { getNoisePresentation } from './getNoisePresentation';
import { getNativeMarker } from './getNativeMarker';

import { EMPTY_ARRAY } from 'shared/constants';

type LayerCoordinates = {
    geometry: {
        coordinates: [number, number, number];
    };
    data: any;
}[];
interface MapLayer {
  layerProvider: MarkersProvider;
  layerType?: string;
  data?: any[];
  coordinates: LayerCoordinates;
}

type PointermoveHandler = (event:any) => void;
type DragStartHandler = (event:any) => void;

export class HereMarker implements HereMarkerInterface {
  private _map?: HereMapInterface;
  private _mapLayers: { [layerName: string]: MapLayer; } = {};
  private _pointermoveHandlers: { [layerName: string]: PointermoveHandler; } = {};
  private _dragStartHandlers: { [layerName: string]: DragStartHandler; } = {};

  constructor(map?: HereMapInterface) {
    this._map = map;

    this._initPointermoveHandler();
    this._initDragStartHandler();
  }

  updateLayers(layers: HereLayers) {
    layers.forEach((layer) => {
      const mapLayer = this._mapLayers[layer.name];

      if (mapLayer) {
        if (mapLayer.layerType === 'native-marker') {
          const nativeMarkerLayer = this._updateNativeMarkerLayer(layer, mapLayer);
          // @ts-ignore
          this._mapLayers[layer.name] = nativeMarkerLayer;
          return;
        }

        if (layer.data.length) {
          const clusterLayer = this._updateClusterLayer(layer, mapLayer);
          this._mapLayers[layer.name] = clusterLayer;
        } else {
          mapLayer.layerProvider.resetProvider();
          delete this._mapLayers[layer.name];
        }
      } else {
        if (layer.type === 'native-marker') {
          const nativeMarkerLayer = this._createNativeMarkerLayer(layer);
          // @ts-ignore
          this._mapLayers[layer.name] = nativeMarkerLayer;
          return;
        }

        if (layer.data.length) {
          const clusterLayer = this._createClusterLayer(layer);
          this._mapLayers[layer.name] = clusterLayer;
        }
      }
    });
  }

  _createNativeMarkerLayer({
    data = EMPTY_ARRAY,
    getCoordinates,
    getIcon,
    onClick,
    onPointermove,
    onDragStart,
    name,
    type
  }: HereLayer) {
    const iconsCache = {};

    const nativeIcons = data.map((item, index) => {
      const { longitude, latitude, altitude } = getCoordinates({ data: item });
      return getNativeMarker(
        getIcon,
        onClick,
        {
          data: item,
          position: { lng: longitude, lat: latitude, alt: altitude },
          iconsCache,
          index
        },
        name
      );
    });

    const group = new H.map.Group({ objects: nativeIcons });
    this._map?.getNativeMap().addObject(group);

    this._addPointermoveHandler(name, onPointermove);
    this._addDragStartHandler(name, onDragStart);

    return { data, group, layerType: type, iconsCache };
  }

  _updateNativeMarkerLayer({
    type,
    data = EMPTY_ARRAY,
    getCoordinates,
    getIcon,
    onClick,
    onPointermove,
    onDragStart,
    name
  }: HereLayer,
  {
    data: prevData,
    group,
    iconsCache
  }: any) {
    const nativeIcons = data.map((item, index) => {
      const { longitude, latitude, altitude } = getCoordinates({ data: item });
      return getNativeMarker(
        getIcon,
        onClick,
        {
          data: item,
          position: { lng: longitude, lat: latitude, alt: altitude },
          iconsCache,
          index
        },
        name
      );
    });

    let newGroup = group;

    if (data.length !== prevData.length) {
      this._map?.getNativeMap().removeObject(group);

      newGroup = new H.map.Group({ objects: nativeIcons });
      this._map?.getNativeMap().addObject(newGroup);
    }

    this._addPointermoveHandler(name, onPointermove);
    this._addDragStartHandler(name, onDragStart);

    return { data, group: newGroup, layerType: type, iconsCache };
  }

  _updateClusterLayer(
    {
      type,
      clusterZoom,
      data = EMPTY_ARRAY,
      getCoordinates,
      getIcon,
      onClick,
      onClusteredData,
      onPointermove,
      onDragStart,
      name
    }: HereLayer,
    {
      layerProvider,
      data: prevData,
      coordinates
    }: MapLayer
  ) {

    layerProvider.getClusterPresentation = (props) => {
      return getClusterPresentation(getIcon, props, name);
    };
    layerProvider.getNoisePresentation = (props) => {
      return getNoisePresentation(getIcon, onClick, props, name);
    };

    layerProvider.onClusteredData = onClusteredData;

    let newCoordinates: LayerCoordinates = coordinates;

    if (data === prevData) {
      layerProvider.updateDataPoints(coordinates, { type, clusterZoom, isUpdateLayer: false });
    } else {
      newCoordinates = data.map(item => {
        const { longitude, latitude, altitude } = getCoordinates({ data: item });
        return {
          geometry: {
            coordinates: [ longitude, latitude, altitude || 0 ]
          },
          data: item
        };
      });

      layerProvider.updateDataPoints(newCoordinates, { type, clusterZoom });
    }

    this._addPointermoveHandler(name, onPointermove);
    this._addDragStartHandler(name, onDragStart);

    return { layerProvider, layerType: type, data, coordinates: newCoordinates };
  }

  _createClusterLayer({
    name,
    type,
    data = EMPTY_ARRAY,
    clusterZoom,
    getCoordinates,
    getIcon,
    onClick,
    onClusteredData,
    onPointermove,
    onDragStart
  }: HereLayer) {
    const layerProvider = new MarkersProvider({
      data,
      map: this._map?.getNativeMap(),
      getClusterPresentation: (props) => {
        return getClusterPresentation(getIcon, props, name);
      },
      getNoisePresentation: (props) => {
        return getNoisePresentation(getIcon, onClick, props, name);
      },
      type,
      clusterZoom,
      onClusteredData
    });

    const coordinates = data.map(item => {
      const { longitude, latitude, altitude } = getCoordinates({ data: item });
      return {
        geometry: {
          coordinates: [ longitude, latitude, altitude || 0 ] as [number, number, number]
        },
        data: item
      };
    });

    layerProvider.updateDataPoints(
      coordinates,
      { type }
    );

    this._addPointermoveHandler(name, onPointermove);
    this._addDragStartHandler(name, onDragStart);

    return { layerProvider, layerType: type, data, coordinates };
  }

  _initPointermoveHandler() {
    this._map?.getNativeMap().addEventListener('pointermove', ({ type, target, originalEvent }: any) => {
      const marker = target && target.getData ? target.getData() || {} : {};
      const event = {
        type,
        data: marker.data,
        markerType: marker.type,
        pageX: originalEvent.pageX,
        pageY: originalEvent.pageY
      };

      Object.values(this._pointermoveHandlers).forEach((pointermoveHandler) => {
        pointermoveHandler(event);
      });
    });
  }

  _addPointermoveHandler(layerName?: string, pointermoveHandler?: PointermoveHandler) {
    if (layerName && pointermoveHandler) {
      this._pointermoveHandlers[layerName] = pointermoveHandler;
    }

    if (layerName && this._pointermoveHandlers[layerName] && !pointermoveHandler) {
      delete this._pointermoveHandlers[layerName];
    }
  }

  _initDragStartHandler() {
    this._map?.getNativeMap().addEventListener('dragstart', ({ type, target, originalEvent }: any) => {
      const marker = target && target.getData ? target.getData() || {} : {};
      const event = {
        type,
        data: marker.data,
        markerType: marker.type,
        pageX: originalEvent.pageX,
        pageY: originalEvent.pageY
      };

      Object.values(this._dragStartHandlers).forEach((dragStartHandler) => {
        dragStartHandler(event);
      });
    });
  }

  _addDragStartHandler(layerName?: string, dragStartHandler?: DragStartHandler) {
    if (layerName && dragStartHandler) {
      this._dragStartHandlers[layerName] = dragStartHandler;
    }

    if (layerName && this._dragStartHandlers[layerName] && !dragStartHandler) {
      delete this._dragStartHandlers[layerName];
    }
  }


  deleteMarker() {}

  setOverlayProps(props: OverlayProps) {}

  addClickHandler(handler: GoogleMapsOverlayProps['onClick']) {
  }

  deleteClickHandler(handler: GoogleMapsOverlayProps['onClick']) {

  };
}